import React, { FC, useEffect, useState } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { FeedbackCommemts } from "../AppraisalUI";
import { useMutation } from "@tanstack/react-query";
import { getUrlPathId } from "../../utils/url-id";
import { apiGetAllEmployeeAppraisal } from "../../api/appraisal";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";
import { useLocation } from "react-router-dom";
import EmployeeFeedbackTable from "./EmployeeFeedbackTable";
import ModalContainer from "../UIElements/modal-container";
import { dummyDataFeedback, FeedBackData } from "../../interface/employment";
import { labelSty } from "../UIElements/styles-input";
import { headerSty, textSty } from "../UIElements/styles-typography";

const EmployeeFeedback: FC<{
  refresh: boolean;
}> = ({ refresh }) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");

  const { showNotification } = useNotificationService();
  const location = useLocation();
  const [feedback_list, setFeedback_list] = useState<FeedBackData[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedData, setSelectedData] =
    useState<FeedBackData>(dummyDataFeedback);

  const handleModal = () => setOpen(!open);
  const handleModalData = (data: FeedBackData) => {
    setSelectedData(data);
  };

  useEffect(() => {
    const applicantId = getUrlPathId(location?.pathname);
    const fields = { employeeId: applicantId };
    mutation.mutate(fields);
  }, [refresh]);

  const mutation = useMutation({
    mutationFn: (fields: { employeeId: string }) =>
      apiGetAllEmployeeAppraisal(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (data?.data?.data?.length > 0) {
          setFeedback_list(data?.data?.data);
        } else {
          setFeedback_list([]);
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });

  return (
    <Box>
      <FeedbackModal
        open={open}
        handleModal={handleModal}
        selectedData={selectedData}
      />
      <EmployeeFeedbackTable
        data={feedback_list}
        loading={mutation.isPending}
        handleModal={handleModal}
        setSelectedData={setSelectedData}
      />
    </Box>
  );
};

export default EmployeeFeedback;

// Modal

export const FeedbackModal: FC<{
  open: boolean;
  handleModal: () => void;
  selectedData: FeedBackData;
}> = ({ open, handleModal, selectedData }) => {
  const isMedium = useResponsive("down", "md", "xl");

  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"md"}
      openModal={open}
      fullWidth={true}
      handleModal={handleModal}
    >
      <Box
        sx={{
          background: "#fff",
          padding: 3,
          borderRadius: "16px",
        }}
      >
        <Stack>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              color: "#292925",
              marginBottom: 3,
            }}
          >
            Employee Appraisal
          </Typography>
          <Box>
            <Stack
              direction={"row"}
              alignItems={"center"}
              sx={{ my: 3, gap: 2 }}
            >
              <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
                Appraisal Type:
              </Typography>
              <Typography sx={{ ...labelSty }}>
                {selectedData?.appraisal_type}
              </Typography>
            </Stack>
            <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
              Medic Information
            </Typography>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...textSty, mb: 0 }}>Name:</Typography>
                <Typography sx={{ ...labelSty }}>
                  {selectedData?.medic_info?.name}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...textSty, mb: 0 }}>Medic Type:</Typography>
                <Typography sx={{ ...labelSty }}>
                  {" "}
                  {selectedData?.medic_info?.medic_type}
                </Typography>
              </Stack>
            </Stack>

            <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle2">
              Appraisal
            </Typography>

            <Stack sx={{ width: "100%", my: 2 }}>
              <Typography sx={{ ...textSty, mb: 0 }}>Comment:</Typography>
              <Typography sx={{ ...labelSty }}>
                {selectedData?.comment}
              </Typography>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...textSty, mb: 0 }}>Goals:</Typography>
                <Typography sx={{ ...labelSty }}>
                  {selectedData?.goals}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...textSty, mb: 0 }}>
                  Overall Summary:
                </Typography>
                <Typography sx={{ ...labelSty }}>
                  {selectedData?.overall_summary}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={isMedium ? "column" : "row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ my: 3, gap: 2 }}
            >
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...textSty, mb: 0 }}>
                  Performance Rating:
                </Typography>
                <Typography sx={{ ...labelSty }}>
                  {selectedData?.performance_rating}
                </Typography>
              </Stack>
              <Stack sx={{ width: isMedium ? "100%" : "49%" }}>
                <Typography sx={{ ...textSty, mb: 0 }}>Strengths:</Typography>
                {selectedData?.strengths?.map((val, id) => (
                  <Box
                    key={id}
                    sx={{ display: "flex", gap: 2, alignItems: "center" }}
                  >
                    <Typography sx={{ ...labelSty }}>{val}</Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={3}
            justifyContent={"flex-end"}
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleModal}
              sx={{ borderRadius: "6px", width: 100 }}
            >
              Close
            </Button>
          </Stack>
        </Stack>
      </Box>
    </ModalContainer>
  );
};
